// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { Point, Rectangle } from '@shared/commons/Geometry';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { Cursors } from '@shared/commons/Cursors';
import { TerceraChartCashItemSeriesDataType } from '../Series/TerceraChartCashItemSeriesEnums';
import { Color, Pen, RectangleEdgeFilter, SolidBrush, Brushes, Pens, PenStyle } from '@shared/commons/Graphics';
import { MathUtils } from '@shared/utils/MathUtils';
import { ChartDataType } from '../Utils/ChartConstants';
import { TerceraChartAction, TerceraChartActionEnum, TerceraChartToolbarsEnum } from '../TerceraChartAction';
import { TerceraChartMainPriceRenderer } from './TerceraChartMainPriceRenderer';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { TradeAnalysisType } from '@shared/utils/History/TFInfo';
import { ColorStyleWidth, DynProperty } from '@shared/commons/DynProperty';
import { LayersEnum } from '../Utils/LayersEnum';
export class TerceraChartDayHighLowRenderer extends TerceraChartBaseRenderer {
    constructor(chart) {
        super(chart);
        this.ShowHighLow_High = '';
        this.ShowHighLow_Low = '';
        this.ShowHighLow_Close = '';
        this.highLowBrush = null;
        this.highLowPen = null;
        this.highLowStyle = PenStyle.ShapedChart;
        this.highLowWidth = 1;
        this.prevCloseBrush = null;
        this.prevClosePen = null;
        this.prevCloseStyle = PenStyle.ShapedChart;
        this.prevCloseWidth = 1;
        this.allHighLow = [];
        this.isHighHovered = false;
        this.isLowHovered = false;
        this.isLineHighVisible = true;
        this.isLineLowVisible = true;
        this.mousePosition = Point.Empty();
        this._Visible = false;
        this.prevCloseVisible = false;
        this.Localize();
        this.assignLayer = LayersEnum.Quotes;
        this.SetClassName('TerceraChartDayHighLowRenderer');
    }
    Enabled() {
        const chart = this.chart;
        if (!chart)
            return false;
        const tfi = chart.TimeFrameInfo();
        if (!tfi)
            return false;
        const isChartDataTypeOk = tfi.ChartDataType === ChartDataType.Default || tfi.ChartDataType === ChartDataType.HeikinAshi;
        return isChartDataTypeOk && tfi.TradeAnalysisType() === TradeAnalysisType.None;
    }
    PrevCloseVisibleGet() {
        return this.prevCloseVisible && this.Enabled();
    }
    VisibleGet() {
        return this.Visible && this.Enabled();
    }
    Localize() {
        this.ShowHighLow_High = Resources.getResource('chart.ShowHighLow.High');
        this.ShowHighLow_Low = Resources.getResource('chart.ShowHighLow.Low');
        this.ShowHighLow_Close = Resources.getResource('chart.ShowHighLow.Close');
    }
    ThemeChanged() {
        this.highLowBrush = new SolidBrush(ThemeManager.colors.Chart_HighLow);
        this.highLowPen = new Pen(ThemeManager.colors.Chart_HighLow, 1, this.highLowStyle);
        this.prevCloseBrush = new SolidBrush(ThemeManager.colors.PrevCloseLineColor);
        this.prevClosePen = new Pen(ThemeManager.colors.PrevCloseLineColor, 1, this.prevCloseStyle);
    }
    ProcessMouseMove(e) {
        if (!this.VisibleGet() && !this.PrevCloseVisibleGet()) {
            return super.ProcessMouseMove(e);
        }
        this.mousePosition = new Point(e.X, e.Y);
        // TODO. Dictionary of layers to redraw?
        // e.NeedRedraw = true;
        return super.ProcessMouseMove(e);
    }
    ProcessMouseDown(e) {
        if (this.isHighHovered) {
            this.isLineHighVisible = !this.isLineHighVisible;
        }
        if (this.isLowHovered) {
            this.isLineLowVisible = !this.isLineLowVisible;
        }
        // TODO. Dictionary of layers to redraw?
        // e.NeedRedraw = true;
        return super.ProcessMouseDown(e);
    }
    GetCursor(e) {
        if (this.isHighHovered || this.isLowHovered) {
            return Cursors.Hand;
        }
        return super.GetCursor(e);
    }
    FindMinMax(out_minMax, window) {
        const chart = this.chart;
        const cashItemSeries = chart.MainCashItemSeries;
        const cashItem = cashItemSeries._cashItem;
        const dataType = chart.model.GetTimeFrameInfo().HistoryType;
        const ins = cashItem.Instrument;
        if (!ins)
            return false;
        if (ins.GetLow(dataType)) {
            out_minMax.tMin = chart.cashItemSeriesSettings.CalculateConverter(ins.GetLow(dataType));
        }
        else {
            out_minMax.tMin = cashItem.dayLow || ins.Low;
        }
        if (ins.GetHigh(dataType)) {
            out_minMax.tMax = chart.cashItemSeriesSettings.CalculateConverter(ins.GetHigh(dataType));
        }
        else {
            out_minMax.tMax = cashItem.dayHigh || ins.High;
        }
        return true;
    }
    Dispose() {
        this.chart = null;
        super.Dispose();
    }
    IsCanPlaced(lastX, item) {
        if (item.Type == HighLowType.PrevClose) {
            const allHighLow = this.allHighLow;
            const len = allHighLow.length;
            for (let j = 0; j < len; j++) {
                const i = allHighLow[j];
                if (item.RightBorder > i.LeftBorder - 5) {
                    if (item.GetYTop() > i.GetYTop() && item.GetYTop() < i.GetYBottom()) {
                        item.Y += i.GetYBottom() - item.GetYTop();
                        return true;
                    }
                    else if (item.GetYBottom() > i.GetYTop() && item.GetYBottom() < i.GetYBottom()) {
                        item.Y -= item.GetYBottom() - i.GetYTop();
                        return true;
                    }
                }
            }
            item.isShown = true;
            return true;
        }
        // lastX == -1 - first plate
        if (lastX == -1 || item.RightBorder < lastX - 5) {
            item.isShown = true;
            return true;
        }
        item.isShown = false;
        return false;
    }
    IsItemHovered(item, x, y) {
        if (x <= item.RightBorder && x >= item.LeftBorder && y >= item.GetYTop() && y <= item.GetYBottom()) {
            switch (item.Type) {
                case HighLowType.High:
                    this.isHighHovered = true;
                    break;
                case HighLowType.Low:
                    this.isLowHovered = true;
                    break;
                case HighLowType.PrevClose:
                    break;
            }
            return true;
        }
        return false;
    }
    GetDataWithTextFromCashItemSeries(cashItemSeries, price, priceStr) {
        switch (cashItemSeries.settings.DataType) {
            case TerceraChartCashItemSeriesDataType.Absolute:
                price = MathUtils.Round(price, this.chart.Instrument().getPrecision(price));
                priceStr = this.chart.Instrument().formatPrice(price);
                break;
            case TerceraChartCashItemSeriesDataType.Relative:
                price = cashItemSeries.settings.relativeDataConverter.Calculate(price);
                priceStr = price.toFixed(2) + '%';
                break;
            case TerceraChartCashItemSeriesDataType.Log:
                price = cashItemSeries.settings.logDataConverter.Calculate(price);
                priceStr = this.chart.Instrument().formatPrice(cashItemSeries.settings.logDataConverter.Revert(price));
                break;
            default:
                priceStr = '';
                break;
        }
        return { price, priceStr };
    }
    GetDataFromCashItemSeries(cashItemSeries, price) {
        return this.GetDataWithTextFromCashItemSeries(cashItemSeries, price, '');
    }
    DrawRectangle(gr, item, font) {
        const transparent = this.IsItemHovered(item, this.mousePosition.X, this.mousePosition.Y) ? 255 : 179;
        const highLowBrush = this.highLowBrush;
        const prevCloseBrush = this.prevCloseBrush;
        if (highLowBrush !== null) {
            highLowBrush.Color = Color.FromArgb(transparent, highLowBrush.Color);
        }
        if (prevCloseBrush !== null) {
            prevCloseBrush.Color = Color.FromArgb(transparent, prevCloseBrush.Color);
        }
        let selectedBrush;
        let selectedRectFilter;
        const x = item.LeftBorder + 1;
        const w = item.RightBorder - item.LeftBorder + 10;
        const h = item.Height + 2;
        let y = 0;
        switch (item.Type) {
            case HighLowType.High:
                selectedBrush = highLowBrush;
                selectedRectFilter = RectangleEdgeFilter.All;
                y = item.Y - item.Height - item.deltaY - 5;
                break;
            case HighLowType.Low:
                selectedBrush = highLowBrush;
                selectedRectFilter = RectangleEdgeFilter.All;
                y = item.Y + item.deltaY;
                break;
            case HighLowType.PrevClose:
                selectedBrush = prevCloseBrush;
                selectedRectFilter = RectangleEdgeFilter.All;
                y = item.Y - item.Height - item.deltaY - 5;
                break;
        }
        const rectToDraw = new Rectangle(x, y, w, h);
        gr.RoundRectangle(rectToDraw, 3, selectedBrush, Pens.Transparent, selectedRectFilter);
        gr.DrawString(item.Text, font, Brushes.White, rectToDraw.X + 2 + rectToDraw.Width / 2, rectToDraw.Y + 2 + rectToDraw.Height / 2, 'center', 'middle');
    }
    Draw(gr, window, windowsContainer, advParams) {
        if (!this.VisibleGet() && !this.PrevCloseVisibleGet()) {
            return;
        }
        const p = advParams;
        if (!p)
            return;
        this.isHighHovered = false;
        this.isLowHovered = false;
        const chart = this.chart;
        const dataType = chart.model.GetTimeFrameInfo().HistoryType;
        const cashItemSeries = chart.MainCashItemSeries;
        if (!p.Instrument || !cashItemSeries)
            return;
        const cashItem = cashItemSeries._cashItem;
        const dayHigh = p.Instrument.GetHigh(dataType);
        const dayLow = p.Instrument.GetLow(dataType);
        const clipRect = window.ClientRectangle;
        this.allHighLow = [];
        const allHighLow = this.allHighLow;
        const scX = window.XScale;
        const curX = clipRect.X + clipRect.Width - scX / 2;
        const screenData = cashItemSeries.ChartScreenData;
        const screenDataStorage = screenData.Storage;
        let leftBorder = 0;
        let barW = 1;
        const result = TerceraChartMainPriceRenderer.ProcessBarWidth(window);
        if (result) {
            leftBorder = result.leftBorder;
            barW = result.barW;
        }
        gr.save();
        gr.beginPath();
        gr.rect(clipRect.X, clipRect.Y, clipRect.Width, clipRect.Height);
        gr.clip();
        if (this.VisibleGet()) {
            // Draw the Low line
            if (this.isLineLowVisible && dayLow !== undefined && dayLow !== -1) {
                let lowLinePrice = dayLow;
                const result = this.GetDataFromCashItemSeries(cashItemSeries, lowLinePrice);
                if (result)
                    lowLinePrice = result.price;
                const low = window.PointsConverter.GetScreenY(lowLinePrice);
                gr.DrawLine(this.highLowPen, clipRect.X + 1, low, clipRect.X + clipRect.Width, low);
            }
            // Draw the High line
            if (this.isLineHighVisible && dayHigh !== undefined && dayHigh !== -1) {
                let highLinePrice = dayHigh;
                const result = this.GetDataFromCashItemSeries(cashItemSeries, highLinePrice);
                if (result)
                    highLinePrice = result.price;
                const high = window.PointsConverter.GetScreenY(highLinePrice);
                gr.DrawLine(this.highLowPen, clipRect.X + 1, high, clipRect.X + clipRect.Width, high);
            }
            let screenDataStorageItem = screenData.MaxDayHighIndex >= 0 ? screenDataStorage[screenData.MaxDayHighIndex] : null;
            if (screenDataStorageItem) {
                const price = screenDataStorageItem.High;
                const strHigh = this.FormatHighLowPrice(HighLowType.High, price);
                const x = window.PointsConverter.GetScreenXbyTime(screenDataStorageItem.Time) + barW / 2 + leftBorder;
                const y = window.PointsConverter.GetScreenY(price);
                const w = gr.GetTextWidth(strHigh, p.Font);
                const dayHLItem = new DayHighLowItem(HighLowType.High, x, x + w, strHigh, y);
                dayHLItem.Height = p.Font.Height;
                allHighLow.push(dayHLItem);
            }
            screenDataStorageItem =
                screenData.MinDayLowIndex >= 0 ? screenDataStorage[screenData.MinDayLowIndex] : null;
            if (screenDataStorageItem) {
                const price = screenDataStorageItem.Low;
                const strLow = this.FormatHighLowPrice(HighLowType.Low, price);
                const x = window.PointsConverter.GetScreenXbyTime(screenDataStorageItem.Time) + barW / 2 + leftBorder;
                const y = window.PointsConverter.GetScreenY(price);
                const w = gr.GetTextWidth(strLow, p.Font);
                const dayHLItem = new DayHighLowItem(HighLowType.Low, x, x + w, strLow, y);
                dayHLItem.Height = p.Font.Height;
                allHighLow.push(dayHLItem);
            }
        }
        // Draw Close
        let prevClose = p.Instrument.Close;
        if (this.PrevCloseVisibleGet() && prevClose) {
            let prevCloseStr = '';
            const result = this.GetDataWithTextFromCashItemSeries(cashItemSeries, prevClose, prevCloseStr);
            if (result) {
                prevClose = result.price;
                prevCloseStr = result.priceStr;
            }
            prevCloseStr = this.ShowHighLow_Close + ': ' + prevCloseStr;
            const y = window.PointsConverter.GetScreenY(prevClose);
            const width = gr.GetTextWidth(prevCloseStr, p.Font);
            const dayHLItem = new DayHighLowItem(HighLowType.PrevClose, clipRect.X, clipRect.X + width, prevCloseStr, y);
            dayHLItem.Height = p.Font.Height;
            allHighLow.push(dayHLItem);
            gr.DrawLine(this.prevClosePen, clipRect.X + 1, y, clipRect.X + clipRect.Width, y);
        }
        let lastHighX = -1; // Store the X-coordinates of the last High tooltip drawing
        let lastLowX = -1; // Store the X-coordinates of the last Low tooltip drawing
        // Draw
        const len = allHighLow.length;
        for (let i = 0; i < len; i++) {
            let canPlaced = false;
            switch (allHighLow[i].Type) {
                case HighLowType.High:
                    canPlaced = this.IsCanPlaced(lastHighX, allHighLow[i]);
                    break;
                case HighLowType.Low:
                    canPlaced = this.IsCanPlaced(lastLowX, allHighLow[i]);
                    break;
                case HighLowType.PrevClose:
                    canPlaced = this.IsCanPlaced(lastHighX, allHighLow[i]) && this.IsCanPlaced(lastLowX, allHighLow[i]);
                    break;
            }
            if (canPlaced) {
                this.DrawRectangle(gr, allHighLow[i], p.Font);
                if (allHighLow[i].Type === HighLowType.High) {
                    lastHighX = allHighLow[i].LeftBorder;
                }
                else if (allHighLow[i].Type === HighLowType.Low) {
                    lastLowX = allHighLow[i].LeftBorder;
                }
            }
            if (allHighLow[i].Type !== HighLowType.PrevClose) {
                gr.FillEllipse(this.highLowBrush, allHighLow[i].LeftBorder - 3, allHighLow[i].Y - 3, 6, 6);
            }
        }
        // Reset the clip.
        gr.restore();
    }
    // #region ICaller
    FormatHighLowPrice(type, price) {
        const chart = this.chart;
        const instrument = chart.Instrument();
        let strPrice = instrument.formatPrice(price);
        if (chart.cashItemSeriesSettings.DataType === TerceraChartCashItemSeriesDataType.Log) {
            strPrice = instrument.formatPrice(chart.cashItemSeriesSettings.logDataConverter.Revert(price));
        }
        switch (type) {
            case HighLowType.High:
                strPrice = this.ShowHighLow_High + ': ' + strPrice;
                break;
            case HighLowType.Low:
                strPrice = this.ShowHighLow_Low + ': ' + strPrice;
                break;
        }
        return strPrice;
    }
    Properties() {
        var _a, _b;
        const properties = super.Properties();
        const state = this.chart.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.DayHighLow));
        if (state.Visible) {
            // Highlight Prices
            const SeparatorGroup = '#4#' + Resources.getResource('property.SeparatorGroup.HighlightPrices');
            const colorStyleWidth = new ColorStyleWidth((_a = this.highLowPen) === null || _a === void 0 ? void 0 : _a.Color, this.highLowStyle, this.highLowWidth); // #42690
            colorStyleWidth.CheckBoxVisible = true;
            colorStyleWidth.Checked = this.VisibleGet();
            const prop = new DynProperty('ShowHighLow', colorStyleWidth, DynProperty.COLOR_STYLE_WIDTH, DynProperty.PRICE_SCALE_GROUP);
            prop.enabled = state.Enabled;
            prop.separatorGroup = SeparatorGroup;
            prop.sortIndex = 60;
            properties.push(prop);
        }
        const statePrevClose = this.chart.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.PreviousCloseLine));
        if (statePrevClose.Visible) {
            const SeparatorGroup = '#4#' + Resources.getResource('property.SeparatorGroup.HighlightPrices');
            const colorStyleWidth = new ColorStyleWidth((_b = this.prevClosePen) === null || _b === void 0 ? void 0 : _b.Color, this.prevCloseStyle, this.prevCloseWidth);
            colorStyleWidth.CheckBoxVisible = true;
            colorStyleWidth.Checked = statePrevClose.Active; // this.Visible;  #47366
            const prop = new DynProperty('ShowPrevCloseLine', colorStyleWidth, DynProperty.COLOR_STYLE_WIDTH, DynProperty.PRICE_SCALE_GROUP);
            prop.enabled = statePrevClose.Enabled;
            prop.separatorGroup = SeparatorGroup;
            prop.sortIndex = 50;
            properties.push(prop);
        }
        return properties;
    }
    callBack(properties) {
        let prop = DynProperty.getPropertyByName(properties, 'ShowHighLow');
        if (prop) {
            const csw = prop.value;
            this.highLowBrush = new SolidBrush(csw.Color);
            this.highLowStyle = csw.Style; // #42690
            this.highLowWidth = csw.Width;
            this.highLowPen = new Pen(csw.Color, this.highLowWidth, this.highLowStyle);
            this.Visible = csw.Checked;
        }
        prop = DynProperty.getPropertyByName(properties, 'ShowPrevCloseLine');
        if (prop) {
            const csw = prop.value;
            this.prevCloseBrush = new SolidBrush(csw.Color);
            this.prevCloseStyle = csw.Style; // #42690
            this.prevCloseWidth = csw.Width;
            this.prevClosePen = new Pen(csw.Color, this.prevCloseWidth, this.prevCloseStyle);
            this.prevCloseVisible = csw.Checked;
        }
    }
}
export var HighLowType;
(function (HighLowType) {
    HighLowType[HighLowType["None"] = 0] = "None";
    HighLowType[HighLowType["High"] = 1] = "High";
    HighLowType[HighLowType["Low"] = 2] = "Low";
    HighLowType[HighLowType["PrevClose"] = 3] = "PrevClose";
})(HighLowType || (HighLowType = {}));
export class DayHighLowItem {
    constructor(type, LeftBorder, RightBorder, Text, y) {
        this.IsHover = false;
        this.Height = 0;
        this.deltaY = 5;
        this.isShown = false;
        this.Type = type;
        this.LeftBorder = LeftBorder;
        this.RightBorder = RightBorder;
        this.Text = Text;
        this.Y = y;
    }
    GetYTop() {
        let y = 0;
        switch (this.Type) {
            case HighLowType.High:
            case HighLowType.PrevClose:
                y = this.Y - this.deltaY - this.Height;
                break;
            case HighLowType.Low:
                y = this.Y + this.deltaY;
                break;
        }
        return y;
    }
    GetYBottom() {
        let y = 0;
        switch (this.Type) {
            case HighLowType.High:
            case HighLowType.PrevClose:
                y = this.Y - this.deltaY;
                break;
            case HighLowType.Low:
                y = this.Y + this.deltaY + this.Height;
                break;
        }
        return y;
    }
}
